import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash, FloppyDisk, Wrench } from "@phosphor-icons/react";

const CATEGORIES = [
    { id: "resin",           label: "Resin" },
    { id: "tpu_sheet",       label: "TPU sheet" },
    { id: "retainer_sheet",  label: "Retainer sheet" },
    { id: "ipa",             label: "IPA" },
    { id: "consumables",     label: "Consumables" },
    { id: "packaging",       label: "Packaging" },
    { id: "other",           label: "Other" },
];
const FIELD_TYPES = ["text", "number", "date", "select"];

/**
 * CustomFieldsSettingsTab — admin defines the extra inputs that appear
 * in the Add Material form per category (e.g. tpu_sheet → thickness,
 * hardness, manufacturer).
 *
 * Endpoints:
 *   GET /api/admin/inventory/field-definitions/{category}
 *   PUT /api/admin/inventory/field-definitions/{category}
 */
export function CustomFieldsSettingsTab() {
    const [category, setCategory] = useState("tpu_sheet");
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dirty, setDirty] = useState(false);

    const load = async (cat) => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/inventory/field-definitions/${cat}`);
            setFields(r.data?.fields || []);
            setDirty(false);
        } catch {
            setFields([]);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(category); }, [category]);

    const addField = () => {
        setFields((f) => [...f, { key: "", label: "", type: "text", required: false, options: [], order: f.length }]);
        setDirty(true);
    };
    const updateField = (i, patch) => {
        setFields((f) => f.map((x, j) => j === i ? { ...x, ...patch } : x));
        setDirty(true);
    };
    const removeField = (i) => {
        setFields((f) => f.filter((_, j) => j !== i));
        setDirty(true);
    };
    const save = async () => {
        // Validate front-end before sending
        const keys = new Set();
        for (const f of fields) {
            if (!f.key?.trim() || !f.label?.trim()) {
                toast.error("Every field needs a key and a label"); return;
            }
            const k = f.key.trim().toLowerCase().replace(/\s+/g, "_");
            if (!/^[a-z0-9_]+$/.test(k)) {
                toast.error(`Field key "${f.key}" must be lowercase_snake_case`); return;
            }
            if (keys.has(k)) { toast.error(`Duplicate field key "${k}"`); return; }
            keys.add(k);
            if (f.type === "select" && (!f.options || f.options.length === 0)) {
                toast.error(`Field "${f.label}" is type=select — needs at least one option`); return;
            }
        }
        try {
            await api.put(`/admin/inventory/field-definitions/${category}`, {
                fields: fields.map((f, idx) => ({
                    ...f,
                    key: f.key.trim().toLowerCase().replace(/\s+/g, "_"),
                    label: f.label.trim(),
                    options: f.type === "select" ? (f.options || []).filter((o) => o.trim()) : [],
                    order: idx,
                })),
            });
            toast.success(`Saved ${fields.length} field${fields.length === 1 ? "" : "s"} for ${category}`);
            setDirty(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to save");
        }
    };

    return (
        <div className="space-y-4" data-testid="custom-fields-settings-tab">
            <div className="rounded-2xl border border-border bg-card p-4 sm:p-5">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <Wrench size={18} weight="fill" className="text-primary" />
                    </div>
                    <div className="text-sm">
                        <div className="font-display font-bold text-base">Custom material fields per category</div>
                        <div className="text-muted-foreground mt-0.5">
                            Define extra inputs that appear in the <span className="font-semibold">Add Material</span> form. Planner picks the same fields when locking the manufacturing spec, and production tech sees them at thermoforming time. Keep keys stable — they're used as object keys in the DB.
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider m-0">Category</Label>
                    <select
                        className="h-10 rounded-md border border-input bg-card px-3 text-sm"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        data-testid="field-defs-category-select"
                    >
                        {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" className="rounded-full" onClick={addField} data-testid="field-defs-add">
                        <Plus size={14} className="mr-1.5" /> Add field
                    </Button>
                    <Button
                        onClick={save}
                        disabled={!dirty}
                        className="rounded-full"
                        data-testid="field-defs-save"
                    >
                        <FloppyDisk size={14} className="mr-1.5" weight="fill" /> Save
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="py-10 text-center text-muted-foreground text-sm">Loading…</div>
            ) : fields.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-card/50 p-8 text-center" data-testid="field-defs-empty">
                    <div className="text-sm text-muted-foreground">No custom fields yet for this category. Click "Add field" to start.</div>
                </div>
            ) : (
                <div className="space-y-3">
                    {fields.map((f, i) => (
                        <div
                            key={i}
                            className="rounded-2xl border border-border bg-card p-4 grid grid-cols-1 lg:grid-cols-12 gap-3"
                            data-testid={`field-def-row-${i}`}
                        >
                            <div className="lg:col-span-3">
                                <Label>Key (snake_case)</Label>
                                <Input
                                    value={f.key}
                                    onChange={(e) => updateField(i, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                                    placeholder="thickness_mm"
                                    className="font-mono"
                                    data-testid={`field-def-key-${i}`}
                                />
                            </div>
                            <div className="lg:col-span-3">
                                <Label>Display label</Label>
                                <Input
                                    value={f.label}
                                    onChange={(e) => updateField(i, { label: e.target.value })}
                                    placeholder="Thickness (mm)"
                                    data-testid={`field-def-label-${i}`}
                                />
                            </div>
                            <div className="lg:col-span-2">
                                <Label>Type</Label>
                                <select
                                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                                    value={f.type}
                                    onChange={(e) => updateField(i, { type: e.target.value, options: e.target.value === "select" ? (f.options || []) : [] })}
                                    data-testid={`field-def-type-${i}`}
                                >
                                    {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className="lg:col-span-2 flex items-end gap-1.5 pb-1">
                                <label className="flex items-center gap-1.5 text-xs font-medium pt-2" data-testid={`field-def-required-${i}`}>
                                    <input
                                        type="checkbox"
                                        checked={!!f.required}
                                        onChange={(e) => updateField(i, { required: e.target.checked })}
                                    />
                                    Required
                                </label>
                            </div>
                            <div className="lg:col-span-2 flex items-start justify-end gap-1 pt-6">
                                <button
                                    type="button"
                                    onClick={() => removeField(i)}
                                    className="p-1.5 hover:bg-rose-50 rounded text-rose-600"
                                    title="Remove field"
                                    data-testid={`field-def-remove-${i}`}
                                >
                                    <Trash size={15} />
                                </button>
                            </div>
                            {f.type === "select" && (
                                <div className="lg:col-span-12 mt-1">
                                    <Label>Options (one per line)</Label>
                                    <textarea
                                        rows={3}
                                        value={(f.options || []).join("\n")}
                                        onChange={(e) => updateField(i, { options: e.target.value.split("\n") })}
                                        placeholder={"Bay Materials\nScheu Dental\nErkodent\nOther"}
                                        className="w-full text-sm rounded-md border border-input bg-background px-3 py-2 font-mono"
                                        data-testid={`field-def-options-${i}`}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default CustomFieldsSettingsTab;
